import React from "react";
import { useDispatch, useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { selectDetails } from "@dashboard/devices/store/selectors/details";
import { closeBatchMode, closeDetails } from "@devices/store";
import { selectBatchActionIsActive, selectBatchActionType } from "@devices/store/selectors";
import { Box } from "@mui/material";

import { ClientSidePanel } from "./ClientPanel/ClientPanel";
import { CreateRolloutSidePanel } from "./CreateRolloutSidePanel/CreateRolloutSidePanel";
import { GroupSidePanel } from "./GroupPanel/GroupPanel";
import { LicenseSidePanel } from "./LicensePanel/LicensePanel";
import { ModulePanel } from "./ModulePanel/ModulePanel";
import { MoveLicensePanel } from "./MoveLicensePanel/MoveLicensePanel";
import { RolloutSidePanel } from "./RolloutSidePanel/RolloutSidePanel";

const useStyle = makeStyles()((theme) => ({
    container: {
        position: "fixed",
        top: 0,
        right: 0,
        zIndex: 10,
        height: "100vh",
        width: 920,
        borderLeft: "1px solid" + theme.palette.emteria.border.primary,
        flexDirection: "column",
        backgroundColor: theme.palette.emteria.background.paperDeep,
    },
}));

export const SidePanel = (props: { refetchClientsList?: () => Promise<void> }) => {
    const ref = React.useRef<HTMLDivElement>(null);

    const dispatch = useDispatch();
    const { classes } = useStyle();

    const batchActionType = useSelector(selectBatchActionType);
    const isBatchActiveActive = useSelector(selectBatchActionIsActive);

    const details = useSelector(selectDetails);

    const isVisible = isBatchActiveActive || details;
    const type = details?.type;

    const handleKeyPress = React.useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                dispatch(closeDetails());
                dispatch(closeBatchMode());
            }
        },
        [dispatch],
    );

    const handleClickOutside = React.useCallback(
        (event: MouseEvent) => {
            const panelNode = ref.current;
            const isInsidePanel = panelNode?.contains(event.target as Node);

            const dialogContainers = document.querySelectorAll(".MuiDialog-container");
            const isInsideDialog = Array.from(dialogContainers).some((dialog) => dialog.contains(event.target as Node));

            const popoverContainers = document.querySelectorAll(".MuiPopover-root");
            const isInsidePopover = Array.from(popoverContainers).some((popover) => popover.contains(event.target as Node));

            const popperContainers = document.querySelectorAll(".MuiPopper-root");
            const isInsidePopper = Array.from(popperContainers).some((popper) => popper.contains(event.target as Node));

            const target = event.target as HTMLElement;
            const isToastClick = target.closest("[role=alert]") !== null;
            const isTooltipClick = target.closest(".MuiTooltip-popper") !== null;
            const isMenuListClick = target.closest("#menu-list-grow") !== null;
            const isPaperClick = target.closest(".MuiPaper-root") !== null && target.closest(".MuiPopper-root") !== null;
            const isDatePickerClick = target.closest("#date-picker-portal") !== null;
            const cell = target.closest("[data-column-id]");
            const isMoveLicenseCheckbox = cell?.getAttribute("data-column-id") === "moveLicenseCheckbox";
            const isOutsidePanel =
                !isInsidePanel &&
                !isInsideDialog &&
                !isInsidePopover &&
                !isInsidePopper &&
                !isToastClick &&
                !isTooltipClick &&
                !isMenuListClick &&
                !isPaperClick &&
                !isDatePickerClick;

            if (batchActionType === "move" && !isMoveLicenseCheckbox && isOutsidePanel && isVisible) {
                dispatch(closeDetails());
                dispatch(closeBatchMode());
            }

            if (batchActionType !== "move" && isOutsidePanel && isVisible) {
                dispatch(closeDetails());
                dispatch(closeBatchMode());
            }
        },
        [batchActionType, dispatch, isVisible],
    );

    React.useEffect(() => {
        document.addEventListener("keydown", handleKeyPress);
        return () => {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [handleKeyPress]);

    React.useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    return (
        <Box ref={ref} className={classes.container} style={{ display: isVisible ? "flex" : "none" }}>
            {!batchActionType && type === "group" ? <GroupSidePanel /> : null}
            {!batchActionType && type === "license" ? <LicenseSidePanel /> : null}
            {!batchActionType && type === "client" ? <ClientSidePanel refetchClientsList={props.refetchClientsList} /> : null}
            {!batchActionType && type === "create-rollout" ? <CreateRolloutSidePanel /> : null}
            {!batchActionType && type === "module" && details?.selectedId ? <ModulePanel productId={details.selectedId} /> : null}
            {!batchActionType && type === "rollout" ? <RolloutSidePanel /> : null}
            {batchActionType === "move" ? <MoveLicensePanel /> : null}
        </Box>
    );
};
